import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import StorageIcon from "@mui/icons-material/Storage";
import WarningIcon from "@mui/icons-material/Warning";
import {
  Snackbar,
  Alert,
  Paper,
  Box,
  CircularProgress,
  Typography,
  Button,
  Chip,
  Divider,
 Tab, Tabs } from "@mui/material";
import { useQuery, useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  getHealthQueryOptions,
  getReindexMutationOptions,
} from "../../../api/documentos/endpoints";
import { useAuth } from "../../../auth";
import AuditIndexPanel from "../../../components/Admin/AuditIndexPanel";
import ProcessIndexPanel from "../../../components/Admin/ProcessIndexPanel";
import StatCard from "../../../components/Admin/StatCard";
import { ConfirmModal } from "../../../components/ConfirmModal";
import ListLayout from "../../../components/Layout/ListLayout";
import { usePermissions } from "../../../hooks/usePermissions";

export const Route = createFileRoute("/__auth/admin/search-index")({
  component: SearchIndexComponent,
  beforeLoad: ({ context }) => {
    if (context.auth.isLoading) return;
    if (!context.auth.user?.isAdmin) {
      throw redirect({
        to: "/dashboard",
      });
    }
  },
  staticData: {
    title: "routes.searchIndex",
  },
});

function getDiffColor(diffPercent: number) {
  if (diffPercent < 5) return { color: "success.main", bgColor: "success.lighter" };
  if (diffPercent < 20) return { color: "warning.main", bgColor: "warning.lighter" };
  return { color: "error.main", bgColor: "error.lighter" };
}

function SearchIndexComponent() {
  const { t } = useTranslation();
  const { currentTenant } = useAuth();
  const { isAdmin, isLoading: isLoadingPermissions } = usePermissions();

  const effectiveTenant = currentTenant || "default-domain";

  const [aba, setAba] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error" | "info";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const showSnackbar = (
    message: string,
    severity: "success" | "error" | "info" = "success"
  ) => {
    setSnackbar({ open: true, message, severity });
  };

  const {
    data: healthData,
    isLoading: isLoadingHealth,
    error: healthError,
    refetch: refetchHealth,
  } = useQuery({
    ...getHealthQueryOptions(effectiveTenant),
    refetchInterval: 30000,
  });

  const healthInfo = (healthData as any)?.data || (healthData as any) || {};
  const dbCount = healthInfo.dbCount ?? 0;
  const indexCount = healthInfo.indexCount ?? 0;
  const diffPercent = healthInfo.diffPercent ?? 0;
  const healthy = healthInfo.healthy ?? true;
  const healthMessage = healthInfo.message;

  const reindexMutation = useMutation({
    ...getReindexMutationOptions(),
    onSuccess: (response: any) => {
      const data = response?.data || response || {};
      const published = data.published ?? data.totalDocuments ?? 0;
      showSnackbar(
        t("admin.searchIndex.success", { published }),
        "success"
      );
      setTimeout(() => refetchHealth(), 2000);
    },
    onError: (error: any) => {
      const status = error?.response?.status || error?.status;
      if (status === 409) {
        showSnackbar(t("admin.searchIndex.alreadyRunning"), "info");
      } else {
        const errMsg =
          error?.body?.message ||
          error?.response?.data?.message ||
          error?.message ||
          t("admin.searchIndex.error");
        showSnackbar(errMsg, "error");
      }
    },
  });

  const handleReindex = () => {
    setConfirmOpen(false);
    reindexMutation.mutate({ tenant: effectiveTenant });
  };

  if (isLoadingPermissions) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 4, mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!isAdmin) {
    return null;
  }

  const diffColors = getDiffColor(diffPercent);

  return (
    <ListLayout
      title={t("admin.searchIndex.title")}
      subtitle={t("admin.searchIndex.subtitle")}
      isLoading={aba === 0 && isLoadingHealth}
    >
      <Tabs
        value={aba}
        onChange={(_, valor) => setAba(valor)}
        sx={{ mb: 3, borderBottom: 1, borderColor: "divider" }}
      >
        <Tab label={t("admin.searchIndex.tabDocuments")} sx={{ textTransform: "none" }} />
        <Tab label={t("admin.searchIndex.tabProcesses")} sx={{ textTransform: "none" }} />
        <Tab label={t("admin.searchIndex.tabAudit")} sx={{ textTransform: "none" }} />
      </Tabs>

      {aba === 1 && (
        <ProcessIndexPanel tenant={effectiveTenant} onNotify={showSnackbar} />
      )}

      {aba === 2 && (
        <AuditIndexPanel tenant={effectiveTenant} onNotify={showSnackbar} />
      )}

      {aba === 0 && (
      <>
      <Box
        sx={{
          display: "flex",
          gap: 3,
          flexWrap: "wrap",
          mb: 3,
        }}
      >
        <StatCard
          title={t("admin.searchIndex.dbCount")}
          value={isLoadingHealth ? "..." : dbCount.toLocaleString()}
          icon={<StorageIcon sx={{ fontSize: 28 }} />}
          color="#1976d2"
          bgColor="rgba(25, 118, 210, 0.08)"
        />
        <StatCard
          title={t("admin.searchIndex.indexCount")}
          value={
            isLoadingHealth
              ? "..."
              : indexCount < 0
                ? t("admin.searchIndex.unavailable")
                : indexCount.toLocaleString()
          }
          icon={<SearchIcon sx={{ fontSize: 28 }} />}
          color="#2e7d32"
          bgColor="rgba(46, 125, 50, 0.08)"
        />
        <StatCard
          title={t("admin.searchIndex.difference")}
          value={isLoadingHealth ? "..." : `${diffPercent}%`}
          icon={<CompareArrowsIcon sx={{ fontSize: 28 }} />}
          color={
            diffPercent < 5
              ? "#2e7d32"
              : diffPercent < 20
                ? "#ed6c02"
                : "#d32f2f"
          }
          bgColor={
            diffPercent < 5
              ? "rgba(46, 125, 50, 0.08)"
              : diffPercent < 20
                ? "rgba(237, 108, 2, 0.08)"
                : "rgba(211, 47, 47, 0.08)"
          }
        />
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          boxShadow: (theme) => theme.shadows[2],
          mb: 3,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t("admin.searchIndex.healthStatus")}
            </Typography>
            {!isLoadingHealth && (
              <Chip
                icon={
                  healthy ? (
                    <CheckCircleIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <WarningIcon sx={{ fontSize: 18 }} />
                  )
                }
                label={
                  healthy
                    ? t("admin.searchIndex.healthy")
                    : t("admin.searchIndex.unhealthy")
                }
                color={healthy ? "success" : "error"}
                variant="filled"
                size="small"
              />
            )}
          </Box>
          {!isLoadingHealth && !healthy && healthMessage && (
            <Typography variant="body2" color="error.main">
              {healthMessage}
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          p: 3,
          borderRadius: 3,
          boxShadow: (theme) => theme.shadows[2],
        }}
      >
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          {t("admin.searchIndex.reindexButton")}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ mb: 2 }}
        >
          {t("admin.searchIndex.description")}
        </Typography>

        <Alert severity="warning" sx={{ mb: 3 }}>
          {t("admin.searchIndex.warning")}
        </Alert>

        <Button
          variant="contained"
          color="primary"
          startIcon={
            reindexMutation.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <RefreshIcon />
            )
          }
          onClick={() => setConfirmOpen(true)}
          disabled={reindexMutation.isPending}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            px: 3,
            py: 1,
          }}
        >
          {reindexMutation.isPending
            ? `${t("admin.searchIndex.reindexButton")}...`
            : t("admin.searchIndex.reindexButton")}
        </Button>
      </Paper>

      </>
      )}

      <ConfirmModal
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleReindex}
        title={t("admin.searchIndex.confirmTitle")}
        description={t("admin.searchIndex.confirmDescription")}
        confirmText={t("admin.searchIndex.reindexButton")}
        cancelText={t("common.cancel")}
        confirmColor="primary"
        isLoading={reindexMutation.isPending}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={8000}
        onClose={() =>
          setSnackbar((prev) => ({ ...prev, open: false }))
        }
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() =>
            setSnackbar((prev) => ({ ...prev, open: false }))
          }
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </ListLayout>
  );
}
